import type { Dispatch, SetStateAction } from "react";
import { BoundedInt } from "../boundedInt";
import { LIMITS } from "../constants";
import { DepartmentFieldAccess } from "../departmentFields";
import { cx } from "../cx";
import type { ExtractFillMap } from "../extractFill";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { DepartmentConfig, FormState } from "../types";
import type { Errors } from "../validation";
import { CargoLines } from "./CargoLines";
import { TextInput } from "./fields";
import { WizardPage } from "./WizardPage";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  config: DepartmentConfig;
  errors: Errors;
  hasErrors: boolean;
  highlighted: Set<string>;
  extractFill?: ExtractFillMap;
  onBlur: (id: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function ShipmentDetailsStep({
  form,
  setForm,
  config,
  errors,
  hasErrors,
  highlighted,
  extractFill,
  onBlur,
  onBack,
  onContinue,
}: Props) {
  const fields = new DepartmentFieldAccess(config);
  const copy = useQuoteCopy();
  const flash = (id: string) => highlighted.has(id) && "flash";
  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));

  return (
    <WizardPage title={copy.t("page.shipment")} hasErrors={hasErrors} onBack={onBack} onContinue={onContinue}>
      <section className="nv-section">
        <h3 className="nv-section-title">{copy.t("page.shipment")}</h3>
        <div className="nv-row">
          <TextInput
            id="nv-goods"
            label={copy.t("shipment.goods")}
            placeholder={copy.t("shipment.goodsPlaceholder")}
            required={fields.isRequired("goods")}
            error={errors.goods}
            fillStatus={extractFill?.status("goods")}
            className={cx("w2", flash("goods"))}
            value={form.goods}
            onChange={(v) => patch({ goods: v })}
            onBlur={() => onBlur("goods")}
          />
          <TextInput
            id="nv-totalWeight"
            label={copy.t("shipment.weight")}
            type="number"
            inputMode="numeric"
            min={LIMITS.totalWeightKg.min}
            max={LIMITS.totalWeightKg.max}
            placeholder="0"
            suffix="kg"
            required={fields.isRequired("totalWeight")}
            error={errors.totalWeight}
            fillStatus={extractFill?.status("totalWeight")}
            className={cx("nv-weight-field", flash("totalWeight"))}
            value={form.totalWeight}
            onChange={(v) =>
              patch({
                totalWeight: BoundedInt.capInput(
                  v,
                  LIMITS.totalWeightKg.min,
                  LIMITS.totalWeightKg.max,
                ),
              })
            }
            onBlur={() => onBlur("totalWeight")}
          />
        </div>
        <CargoLines
          form={form}
          setForm={setForm}
          errors={errors}
          extractFill={extractFill}
          palletTypes={fields.palletTypes()}
          onBlur={onBlur}
        />
      </section>
    </WizardPage>
  );
}
